export const SHIPPING_COST = 6;
export const SPLIT_RATE = 0.8;
export const SERVICE_FEE_RATE = 0.016;

export const ORDER_COLORS = ["黑色ABS哑光", "白色ABS"] as const;

export const SPECIAL_FILE_RULES = [
  { keyword: "BF360", totalPrice: 139, split: 59 },
  { keyword: "扶摇百里", totalPrice: 159, split: 65 },
] as const;

export const ROLES = {
  OWNER: "owner",
  FRIEND: "friend",
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export const ROLE_LABELS: Record<Role, string> = {
  owner: "录单",
  friend: "打印",
};

export interface OrderItem {
  id: number;
  order_id: number;
  file_name: string | null;
  file_key: string | null;
  color: string | null;
  quantity: number;
  is_printed: number;
  sort_order: number;
  downloaded_at: string | null;
  acknowledged_at: string | null;
}

export interface Order {
  id: number;
  order_time: string | null;
  xianyu_id: string | null;
  pickup_code: string | null;
  total_price: number | null;
  pickup_time: string | null;
  split: number | null;
  split_special: number | null;
  is_transferred: number;
  transferred_at: string | null;
  notes: string | null;
  created_at: string;
  items: OrderItem[];
}

export interface SplitSource {
  split: number | null;
  split_special: number | null;
}

export interface CompletionSource {
  order_time: string | null;
  pickup_time: string | null;
  is_transferred: number | boolean;
  items: Array<{ is_printed: number | boolean }>;
}

export type SpecialFileRule = (typeof SPECIAL_FILE_RULES)[number];

export function calculateAutomaticSplit(total: number | null): number {
  const normalized = total ?? 0;
  return Math.round((normalized - SHIPPING_COST) * SPLIT_RATE * 100) / 100;
}

export function effectiveSplit(order: SplitSource): number {
  return order.split_special ?? order.split ?? 0;
}

export function findSpecialFileRule(fileNames: readonly string[]): SpecialFileRule | null {
  return (
    SPECIAL_FILE_RULES.find((rule) =>
      fileNames.some((fileName) => fileName.includes(rule.keyword)),
    ) ?? null
  );
}

export function parsePickupDate(
  pickup: string | null,
  orderTime: string | null,
): Date | null {
  if (!pickup) return null;
  const match = /^\s*(\d{1,2})[.\/-](\d{1,2})\s*$/.exec(pickup);
  if (!match) return null;
  const month = Number.parseInt(match[1], 10);
  const day = Number.parseInt(match[2], 10);
  const orderDate = orderTime ? new Date(orderTime) : null;
  const year = orderDate && !Number.isNaN(orderDate.getTime())
    ? orderDate.getFullYear()
    : new Date().getFullYear();
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

export function isOrderFullyDone(order: CompletionSource, now = new Date()): boolean {
  if (!order.is_transferred || order.items.length === 0) return false;
  if (!order.items.every((item) => Boolean(item.is_printed))) return false;
  const pickupDate = parsePickupDate(order.pickup_time, order.order_time);
  if (!pickupDate) return false;
  const completedAt = new Date(
    pickupDate.getFullYear(),
    pickupDate.getMonth(),
    pickupDate.getDate() + 1,
  );
  return now >= completedAt;
}
